import { Button } from "primereact/button";
import { Column, ColumnEditorOptions } from "primereact/column";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { useEffect, useState, useRef } from "react";
import { Toast } from "primereact/toast";
import { POSRawMaterialModel } from "../../models/all-user/edit-user-model";
import posService from "../../services/pos/pos.service";
import { Tag } from 'primereact/tag';
import RawMaterialService from "../../services/RawMaterials/RawMaterialService";
import { useNavigate } from "react-router-dom";
import RouteConstant from "../../constants/route.constants";
import OutwardRawMaterials from "./Outward-RawMaterials";
import AdjustRawMaterials from "./Adjust-rawMaterials";
import RequestRawMaterial from "./Request-rawMaterials";
// import InwardProduct from "./Inward-product";
// import OutwardProduct from "./outward-product";  
// import AdjustProduct from "./Adjust-product";
// import RequestProduct from "./request-product";

interface DataTableProps {
  data: POSRawMaterialModel[];
  onRefresh: (posId: number) => void;
  setLoading: (loading: boolean) => void;
}

const RawMaterialInventoryDatatable: React.FC<DataTableProps> = ({ data,onRefresh,setLoading }) => {
  const toast = useRef<Toast>(null);
  const [tableData, setTableData] = useState<any[]>([]);
  const [selectedRawmaterials, setselectedRawmaterials] = useState<any | null>(null);
   const [showInwardDialog, setShowInwardDialog] = useState(false);
  const [showOutwardDialog, setShowOutwardDialog] = useState(false);
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const navigate = useNavigate();

  const showMessage = (summary: string, detail: string, severity: "success" | "error") => {
    toast.current?.show({ severity, summary, detail, life: 3000 });
  };

  useEffect(() => {
    setTableData(data);
  }, [data]);

  // Allow empty while typing; block negatives; only digits; coerce to 0 on save
  const safetyEditor = (options: ColumnEditorOptions) => (
    <InputText
      type="text"
      inputMode="numeric"
      pattern="\d*"
      value={options.value === 0 ? "0" : (options.value ?? "")}
      onChange={(e) => {
        const raw = e.target.value ?? "";
        const cleaned = raw.replace(/[^\d]/g, ""); // keep only 0-9
        options.editorCallback?.(cleaned);
      }}
      onPaste={(e) => {
        e.preventDefault();
        const pasted = (e.clipboardData || (window as any).clipboardData).getData("text");
        const cleaned = String(pasted).replace(/[^\d]/g, "");
        options.editorCallback?.(cleaned);
      }}
      style={{ width: '100%' }}
    />
  );

 useEffect(() => {
  const enrichedData = data.map(item => {
    let status = "";
    if (item.currentStock < item.safetyStock) {
      status = "Critical";
    } else if (item.currentStock < item.safetyStock + 10) {
      status = "Low";
    } else {
      status = "Sufficient";
    }

    return { ...item, status };
  });

  setTableData(enrichedData);
}, [data]);

  const reorderEditor = (options: ColumnEditorOptions) => (
    <InputText
      type="text"
      inputMode="numeric"
      pattern="\d*"
      value={options.value === 0 ? "0" : (options.value ?? "")}
      onChange={(e) => {
        const raw = e.target.value ?? "";
        const cleaned = raw.replace(/[^\d]/g, "");
        options.editorCallback?.(cleaned);
      }}
      onPaste={(e) => {
        e.preventDefault();
        const pasted = (e.clipboardData || (window as any).clipboardData).getData("text");
        const cleaned = String(pasted).replace(/[^\d]/g, "");
        options.editorCallback?.(cleaned);
      }}
      style={{ width: '100%' }}
    />
  );

  const onRowEditComplete = async (e: DataTableRowEditCompleteEvent) => {
    const updatedRow = { ...e.newData };

    // Coerce blank/invalid to 0 on save (not while typing)
    const toNumOrZero = (v: any) => {
      if (v === "" || v === null || v === undefined) return 0;
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };
    updatedRow.safetyStock = toNumOrZero(updatedRow.safetyStock);
    updatedRow.reorderStock = toNumOrZero(updatedRow.reorderStock);

    try {
      const updatePayload = {
        rawMaterialId: updatedRow.rawMaterialId,  // Ensure this field exists in your data
        posId: updatedRow.posId,          // Ensure this field exists in your data
        safetyStock: updatedRow.safetyStock,
        reorderStock: updatedRow.reorderStock,
      };

      const response = await RawMaterialService.UpdateposRawmaterials(updatePayload);
      setLoading(true);
      if (response?.isSuccess) {
        const updated = [...tableData];
        const row = { ...updatedRow };
        if (row.currentStock < row.safetyStock) {
          row.status = "Critical";
        } else if (row.currentStock < row.reorderStock) {
          row.status = "Low";
        } else {
          row.status = "Sufficient";
        }
        updated[e.index] = row;
        setTableData(updated);
        
        showMessage("Success", response.message || "Row updated successfully", "success");
      } else {
        showMessage("Error", response?.message || "Failed to update row", "error");
      }
    } catch (error) {
      console.error("Update error:", error);
      showMessage("Error", "An unexpected error occurred while updating.", "error");
    }
    setTimeout(() => setLoading(false), 300);
  };

  const statusBodyTemplate = (rowData: any) => {
    let severity: "danger" | "warning" | "success" | undefined;

    if (rowData.status === "Critical") {
      severity = "danger";     // Red
    } else if (rowData.status === "Low") {
      severity = "warning";    // Yellow
    } else if (rowData.status === "Sufficient") {
      severity = "success";    // Green
    }

    return <Tag value={rowData.status} severity={severity} />;
  };

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <div className="col-12">
      <div className="card border-noround p-2">
        <DataTable
        value={tableData}
        editMode="row"
        onRowEditComplete={onRowEditComplete}
        paginator
        rows={10}
        rowsPerPageOptions={[10, 25, 50, 100]}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        >
          <Column field="rawMaterialName" header="Raw Materials" style={{ width: "15%" }} sortable/>
          <Column field="currentStock" header="Stock" style={{ width: "10%" }} sortable/>
          <Column
            field="measurementTypeName"
            header="Measurement Type"
            style={{ width: "15%" }}
            sortable
          />
          <Column
            field="safetyStock"
            header="Safety"
            editor={safetyEditor}
            style={{ width: "10%" }}
            sortable
          />
          <Column
            field="reorderStock"
            header="Reorder"
            editor={reorderEditor}
            style={{ width: "10%" }}
            sortable
          />
          <Column
            field="status"
            header="Status"
            body={statusBodyTemplate}
            style={{ width: "10%" }}
            sortable
          />
          <Column
            header="Action"
            style={{ width: '30%' }}
            body={(rowData) => (
                <div className="flex flex-wrap gap-2 justify-content-center">
                  {/* <Button
                    label="Purchase"
                    severity="success"
                    text
                    raised
                    className="px-1 py-2 text-sm"
                    // onClick={() => {
                    //   setselectedRawmaterials(rowData);
                    //   setShowInwardDialog(true);
                    // }}
                                        onClick={() => {
                        navigate(RouteConstant.RawMaterialsPurchase);
                    }}
                    
                /> */}
                
                 <Button
                    label="Request"
                    severity="secondary"
                    text
                    raised
                    className="px-1 py-2 text-sm"
                     onClick={() => {
                    setselectedRawmaterials(rowData);
                    setShowRequestDialog(true);
                  }}
                />
                      
                <Button
                    label="Outward"
                    severity="warning"
                    text
                    raised
                    className="px-1 py-2 text-sm"
                    onClick={() => {
                      setselectedRawmaterials(rowData);
                      setShowOutwardDialog(true);
                    }}
                />
                <Button
                    label="Adjust"
                    severity="info"
                    text
                    raised
                    className="px-1 py-2 text-sm"
                    onClick={() => {
                    setselectedRawmaterials(rowData);
                    setShowAdjustDialog(true);
                  }}
                />
                </div>
            )}
            />

          <Column rowEditor header="Edit" bodyStyle={{ textAlign: 'center' }} />
        </DataTable>
         {/* <InwardProduct
          visible={showInwardDialog}
          onHide={() => setShowInwardDialog(false)}
          product={selectedRawmaterials}
          onSuccess={onRefresh}
          setLoading={setLoading}
        />
        
        
        <RequestProduct
          visible={showRequestDialog}
          onHide={() => setShowRequestDialog(false)}
          product={selectedRawmaterials}
          setLoading={setLoading}
        /> */}
        <OutwardRawMaterials
          visible={showOutwardDialog}
          onHide={() => setShowOutwardDialog(false)}
          rawmaterial={selectedRawmaterials}
          onSuccess={onRefresh}
          setLoading={setLoading}
        />
        <AdjustRawMaterials
          visible={showAdjustDialog}
          onHide={() => setShowAdjustDialog(false)}
          rawmaterial={selectedRawmaterials}
          onSuccess={onRefresh}
          setLoading={setLoading}
        />
        <RequestRawMaterial
          visible={showRequestDialog}
          onHide={() => setShowRequestDialog(false)}
          rawmaterial={selectedRawmaterials}
          setLoading={setLoading}
        />
      </div>
      </div>
    </>
  );
};

export default RawMaterialInventoryDatatable;